import React, { ReactNode, useCallback, useState } from "react";

import Switch from "antd/es/switch";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { defineMessages, useIntl } from "react-intl";

import PinLegendDatingMethod from "@mapmycustomers/shared/enum/pinLegends/PinLegendDatingMethod";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend, {
  DateHistogramData,
  PinLegendRange,
} from "@mapmycustomers/shared/types/map/PinLegend";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import { DEFAULT_ITEMS_SHAPE } from "@app/scene/map/components/pinLegends/utils/consts";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "./CustomDateShapeKey.module.scss";
import ShapeBadge from "./ShapeBadge";
import ShapeKey from "./ShapeKey";
import useDateHistogramChartConfig from "./useDateHistogramChartConfig";

const messages = defineMessages({
  displayChart: {
    id: "map.layer.shapeKey.legend.customDate.displayChartToggle",
    defaultMessage: "Display graph",
    description: "Label of the graph toggle switch in Custom Date Shape legend",
  },
  noStats: {
    id: "map.layer.shapeKey.legend.customDate.stats.noData",
    defaultMessage: "No data, please refresh the page to update.",
    description: "No Stats message in Custom Date Shape legend",
  },
  rangeLabel: {
    id: "map.layer.shapeKey.legend.customDate.rangeLabel",
    defaultMessage: "{min} — {max} <span>({count})</span>",
    description: "Range label in Custom Date Shape legend",
  },
  unknown: {
    id: "map.layer.shapeKey.legend.customDate.default",
    defaultMessage: "No date <span>({count})</span>",
    description: "A default option to show in a list of buckets for shape key",
  },
});

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
  pinLegend: PinLegend;
}

const CustomDateShapeKey: React.FC<Props> = ({ pinLegend }) => {
  const intl = useIntl();

  const [chartVisible, setChartVisibility] = useState(false);
  const toggleChartVisibility = useCallback(() => setChartVisibility((visible) => !visible), []);
  const handleSwitchChange = useCallback(
    (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
      stopEvents(event);
      setChartVisibility(checked);
    },
    []
  );

  const valueFormatter = useCallback(
    (value: string) => {
      const datingMethod: PinLegendDatingMethod =
        pinLegend.thematic?.dateTrimming ?? PinLegendDatingMethod.ABSOLUTE;
      const date =
        datingMethod === PinLegendDatingMethod.ORDINAL
          ? parseApiDate(`${new Date().getFullYear()}-${value as string}`)
          : parseApiDate(value.length <= 10 ? `${value as string}T00:00:00Z` : (value as string));
      if (!isValidDate(date)) {
        return value;
      }

      switch (datingMethod) {
        case PinLegendDatingMethod.ABSOLUTE:
        // platform returns ranges in ISO format, so we don't need to calculate ranges ourselves
        // so it is formatted as an Absolute one
        // eslint-disable-next-line no-fallthrough
        case PinLegendDatingMethod.RELATIVE:
          return formatDate(date, "PP");
        case PinLegendDatingMethod.ORDINAL:
          return formatDate(date, "LLL dd");
      }
    },
    [pinLegend.thematic?.dateTrimming]
  );

  const chartConfig = useDateHistogramChartConfig({
    data: (pinLegend.stats?.data?.histogram?.histogram as DateHistogramData) ?? [],
    ranges: pinLegend.stats?.data.ranges,
    visible: chartVisible,
  });

  if (!pinLegend.stats) {
    return <ShapeKey>{intl.formatMessage(messages.noStats)}</ShapeKey>;
  }

  return (
    <div className={styles.container}>
      {chartVisible && (
        <HighchartsReact
          containerProps={{ className: styles.chart }}
          highcharts={Highcharts}
          isPureConfig
          options={chartConfig}
        />
      )}
      <div className={styles.buckets}>
        {pinLegend.thematic!.defaults?.shape && (
          <ShapeBadge
            key="unknown"
            label={intl.formatMessage(messages.unknown, {
              count: pinLegend.stats?.data?.missing ?? 0,
              span: (text: ReactNode[]) => <span className={styles.count}>{text}</span>,
            })}
            shape={pinLegend.thematic!.defaults.shape}
          />
        )}
        {pinLegend.stats.data.ranges.map(([min, max, count]: PinLegendRange, index: number) => (
          <ShapeBadge
            key={index}
            label={intl.formatMessage(messages.rangeLabel, {
              count,
              max: valueFormatter(max as string),
              min: valueFormatter(min as string),
              span: (text: ReactNode[]) => <span className={styles.count}>{text}</span>,
            })}
            shape={pinLegend.thematic!.shapes![index]?.shape ?? DEFAULT_ITEMS_SHAPE}
          />
        ))}
      </div>
      {pinLegend.stats?.data?.histogram && (
        <div className={styles.chartToggle} onClick={toggleChartVisibility}>
          <span>{intl.formatMessage(messages.displayChart)}</span>
          <Switch checked={chartVisible} onChange={handleSwitchChange} />
        </div>
      )}
    </div>
  );
};

export default CustomDateShapeKey;
